import type { ScheduleTaskType } from '@risksmart-app/domain/src/types/consts';
import type { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { StatusType } from 'src/components/form/controlled-select/ControlledSelect';
import { Controller } from 'src/components/form/field-controller/Controller';
import { FormField } from 'src/components/form/form/FormField';
import Select from 'src/components/form/select';

import type { ScheduleFormFieldData } from './scheduleFormSchema';

export interface TaskTypeOption {
  value: ScheduleTaskType;
  label: string;
  description?: string;
}

type Props = {
  options: TaskTypeOption[];
  statusType: StatusType;
};

/**
 * Each type targets exactly one object type, so this choice decides what the
 * object picker below offers. Changing it clears the selection rather than
 * leaving objects of a type the schedule no longer covers.
 */
export const TaskTypeField: FC<Props> = ({ options, statusType }) => {
  const { t } = useTranslation(['common']);
  const { control, setValue } = useFormContext<ScheduleFormFieldData>();
  const { error } = control.getFieldState('TaskType');

  return (
    <Controller
      name={'TaskType'}
      control={control}
      render={({ field: { ref, onChange, onBlur, value } }) => (
        <FormField
          label={t('scheduler.fields.task_type')}
          errorText={error?.message}
          stretch={true}
          testId={'schedule-task-type'}
        >
          <Select
            ref={ref}
            options={options}
            statusType={statusType}
            selectedOption={
              options.find((option) => option.value === value) ?? null
            }
            onBlur={onBlur}
            onChange={(e) => {
              const selected = options.find(
                (option) => option.value === e.detail.selectedOption?.value
              );
              // Cleared in form state rather than filtered at read time: a stale
              // target left in the values reaches the payload through any other
              // reader that forgets the same filter.
              setValue('Targets', []);
              onChange(selected?.value ?? '');
            }}
            placeholder={t('select')}
            empty={t('noMatchedFound')}
          />
        </FormField>
      )}
    />
  );
};
